"use client";

// Burbuja flotante de WhatsApp para las landings de compra.
//
// Dos decisiones que no son de estilo y conviene no tocar sin leer esto:
//
// 1) EL ESTADO SE PIDE SIEMPRE POR GET DESDE EL CLIENTE. Las landings son
//    `force-static` con `revalidate = 3600` y además CloudFront cachea el HTML
//    con s-maxage=3600: si el valor viniera del render de servidor, apagar el
//    botón podría tardar hasta una hora en verse. `api.briancallimusic.com` no
//    tiene CloudFront delante (sale derecho al EC2), así que este GET siempre
//    pega contra el origen y el parámetro se refleja al instante.
//
// 2) ARRANCA OCULTA Y FALLA OCULTA. Mientras no llegue la respuesta, o si la API
//    se cae, o si falta el número, no se renderiza nada. El botón compite con el
//    CTA de compra, así que el default seguro es que no esté.
//
// La burbuja se corre para arriba cuando hay una barra fija de compra abajo (las
// landings tienen barra sticky en mobile). Ver `[data-bcm-sticky-bar]`.

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { trackWhatsappClick } from "@/lib/analytics";

const PARAM_ENABLED = "whatsappButtonEnabled";
const PARAM_NUMBER = "whatsappNumber";

// Separación respecto del borde de la pantalla y de cualquier barra fija inferior.
const GAP = 20;

// Debajo de las barras de compra (z-index 900 y 9000) y de los modales (9999+):
// si algo importante se superpone, gana lo importante.
const Z_INDEX = 800;

const esTrue = (v) => String(v ?? "").trim().toLowerCase() === "true";
const soloDigitos = (v) => String(v ?? "").replace(/[^0-9]/g, "");

// `mensaje` es la clave del saludo prellenado. La landing de la app usa la suya: con el de los
// libros, el respondedor le contestaba la lista de libros a quien preguntaba por la app. Cada
// saludo tiene que estar tal cual en admin-back/app/Support/Wa/SaludoDeLanding.php.
export default function WhatsAppBubble({ source = "web", mensaje = "whatsapp_bubble_message" }) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState("");
  const [bottom, setBottom] = useState(GAP);
  const [montado, setMontado] = useState(false);

  useEffect(() => setMontado(true), []);

  // ── Estado del botón (ver nota 1 arriba) ──
  useEffect(() => {
    let cancelado = false;

    api
      .get("/api/global-parameters/batch", {
        params: { keys: `${PARAM_ENABLED},${PARAM_NUMBER}` },
      })
      .then((res) => {
        if (cancelado) return;
        const data = res?.data || {};
        const numero = soloDigitos(data[PARAM_NUMBER]);
        // Los dos tienen que estar: el flag prendido Y un número usable.
        if (esTrue(data[PARAM_ENABLED]) && numero.length >= 8) setPhone(numero);
      })
      .catch(() => {
        /* la API no contesta: la burbuja queda oculta, que es el default seguro */
      });

    return () => {
      cancelado = true;
    };
  }, []);

  // ── Que no tape la barra fija de compra ──
  // Mide cuánto de la franja inferior del viewport está ocupado por barras fijas
  // y se corre esa cantidad para arriba. Las barras que se esconden con
  // translateY quedan con top >= innerHeight, así que no suman.
  const recalcular = useCallback(() => {
    let ocupado = 0;

    document.querySelectorAll("[data-bcm-sticky-bar]").forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      const pisa = window.innerHeight - r.top;
      if (pisa > 0) ocupado = Math.max(ocupado, Math.min(pisa, r.height));
    });

    setBottom(GAP + ocupado);
  }, []);

  useEffect(() => {
    if (!phone) return undefined;

    let raf = 0;
    const pedir = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        recalcular();
      });
    };

    pedir();
    window.addEventListener("scroll", pedir, { passive: true });
    window.addEventListener("resize", pedir);

    // Las barras no aparecen sólo por scroll (en /arma-tu-pack salen cuando hay
    // selección, en /libros se ocultan cambiando el transform), así que hace
    // falta escuchar el DOM además del scroll. El rAF acota el costo a un
    // recálculo por frame.
    const observer = new MutationObserver(pedir);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", pedir);
      window.removeEventListener("resize", pedir);
      observer.disconnect();
    };
  }, [phone, recalcular]);

  if (!montado || !phone) return null;

  // El `ref` viaja en el texto prellenado: es la única forma de saber qué página
  // trajo cada número, porque wa.me no admite parámetros propios. Se lee después
  // del primer `text_body` entrante en wa_messages.
  const texto = `${t(mensaje)} (ref: ${source})`;
  const href = `https://wa.me/${phone}?text=${encodeURIComponent(texto)}`;

  return createPortal(
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer nofollow"
      aria-label={t("whatsapp_bubble_aria")}
      title={t("whatsapp_bubble_aria")}
      onClick={() => trackWhatsappClick(source)}
      style={{
        position: "fixed",
        right: GAP,
        bottom,
        zIndex: Z_INDEX,
        width: 56,
        height: 56,
        borderRadius: "50%",
        background: "#25D366",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 14px rgba(0,0,0,0.28)",
        transition: "bottom .2s ease",
        // El area de toque en mobile no puede depender del tamaño visual.
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width="30"
        height="30"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
      </svg>
    </a>,
    document.body
  );
}
